import { defineStore } from "pinia";
import { ref } from "vue";

export type ToastKind = "error" | "success" | "info";

export interface Toast {
  id: number;
  message: string;
  kind: ToastKind;
}

// How long a toast stays on screen, and how long an identical message is
// suppressed for. The dedupe window matters because a single page can fire
// several requests in parallel (EventShow alone fans out to ~6 on mount) —
// without it, one dropped connection produces a stack of identical toasts.
const VISIBLE_MS = 6000;
const DEDUPE_MS = 3000;

export const useToastStore = defineStore("toast", () => {
  const toasts = ref<Toast[]>([]);
  const recent = new Map<string, number>();
  let nextId = 0;

  const dismiss = (id: number) => {
    toasts.value = toasts.value.filter((t) => t.id !== id);
  };

  const push = (message: string, kind: ToastKind = "error") => {
    const text = (message || "").trim();
    if (!text) return;

    const now = Date.now();
    const seenAt = recent.get(text);
    if (seenAt !== undefined && now - seenAt < DEDUPE_MS) return;
    recent.set(text, now);

    const id = nextId++;
    toasts.value.push({ id, message: text, kind });
    setTimeout(() => dismiss(id), VISIBLE_MS);
  };

  return { toasts, push, dismiss };
});
